import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import BoltIcon from '@mui/icons-material/Bolt';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocationPinIcon from '@mui/icons-material/LocationPin';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import ReportIcon from '@mui/icons-material/Report';
import SpeakerNotes from '@mui/icons-material/SpeakerNotes';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SwapCallsIcon from '@mui/icons-material/SwapCalls';
import WorkIcon from '@mui/icons-material/Work';
import { alpha, Fade, useScrollTrigger } from '@mui/material';
import React, { useContext, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import {
  AccountUserDetailDTO,
  AgentTypeEnum,
  AppointmentHistoryDetailDTO,
  AppointmentStatusEnum,
  Company,
  isPoolBrainPhoneCall,
  isServiceTitanApiPhoneCall,
  isServiceTitanPortalV2PhoneCall,
  isStCapacityPhoneCall,
  isSwapiV1PhoneCall,
  isSwapiV2PhoneCall,
  ServiceTitanPhoneCallCustomerMembership,
  ServiceTypeEnum,
  ST,
  TimezoneAbbreviationEnum,
  UserDetailDTO
} from '_lib/api/apiTypes.shim';
import { Accordion } from '_lib/components/Accordion/Accordion';
import { AccordionDetails } from '_lib/components/AccordionDetail/AccordionDetail';
import { AccordionSummary } from '_lib/components/AccordionSummary/AccordionSummary';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { InfoAlert } from '_lib/components/Alert/Alert';
import { SimpleAppBar } from '_lib/components/AppBar/SimpleAppBar';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Card } from '_lib/components/Card/Card';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { Chip } from '_lib/components/Chip/Chip';
import { Divider } from '_lib/components/Divider/Divider';
import { Fab } from '_lib/components/Fab/Fab';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { IconButton } from '_lib/components/IconButton/IconButton';
import { List } from '_lib/components/List/List';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { Paper } from '_lib/components/Paper/Paper';
import { ShowMoreText } from '_lib/components/ShowMoreText/ShowMoreText';
import { Stack } from '_lib/components/Stack/Stack';

import { Tab, TabProps } from '_lib/components/Tab/Tab';
import { Tabs } from '_lib/components/Tabs/Tabs';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { AiCallIcon } from '_lib/featureComponents/AiCall/AiCallIcon';
import { ManageCompanies } from '_lib/featureComponents/auth/permissionHelpers';
import { CallSegmentListDisplay } from '_lib/featureComponents/CallSegment/CallSegmentListDisplay';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';
import { getBlockTypeAndTimeDisplay } from '_lib/services/appointmentWindowService';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';
import { formatAddressOneLine, formatAddressTwoLine, formatPhone, sortedNotes, toSxArray } from '_lib/utils/helpers';

import { AppDrawerState, AppRouteContext } from '../../App/app.types';
import { AuthContext } from '../../auth/authContext';
import { CompanyWindowDisplay } from '../../CompanyWindow/CompanyWindowDisplay';
import { HeaderBox } from '../../HeaderBox/HeaderBox';
import { IconTypography } from '../../IconTypography/IconTypography';
import { KeyValueGrid } from '../../KeyValueGrid/KeyValueGrid';
import { hasGoBack, getFullAppRoutePath, appRoutePaths } from '../../Nav/nav.types';
import { NoteList } from '../../NoteList/NoteList';
import { TextBlockTypography } from '../../TextBoxTypography/TextBoxTypography';
import { TradesDisplay } from '../../Trades/TradesDisplay';

import { PhoneCallAiCall } from './PhoneCallAiCall';
import { PhoneCallCompanyTabs } from './PhoneCallCompanyTabs';
import { PhoneCallStAppointment } from './PhoneCallStAppointment';
import { PhoneCallStCustomerDetail } from './PhoneCallStCustomerDetail';
import { PhoneCallTabs } from './PhoneCallTabs';

const getCallAgentDisplayName = (callAgent?: UserDetailDTO | AccountUserDetailDTO) => {
  if (!callAgent) return 'Unknown Call Agent';
  const callAgentName = `${callAgent.firstName} ${callAgent.lastName}`;
  return callAgent.isActive ? callAgentName : `${callAgentName} (Inactive)`;
};

const getCallLogAppointmentEntries = (appointmentHistoryData: AppointmentHistoryDetailDTO, companyData: Company) => [
  {
    label: 'Window Details',
    textOrComponent: (
      <Stack spacing={0}>
        <CompanyWindowDisplay
          companyWindow={appointmentHistoryData.companyWindow}
          isManualEmployeeAssignEnabled={companyData.isManualEmployeeAssignEnabled}
        />
        <IconTypography
          startIcon={<EventIcon color='primary' />}
          text={`${DateUtils.format(appointmentHistoryData.date, DateFormatEnum.DateLongWeekday)} ${getBlockTypeAndTimeDisplay(appointmentHistoryData.appointmentWindow, companyData.timezone)}`}
        />
      </Stack>
    )
  },
  {
    label: 'Window Booking Cut Off Time',
    textOrComponent: (() => {
      const cutOffTime = DateUtils.fromDateAndMinutesToDate(
        new Date(appointmentHistoryData.date),
        appointmentHistoryData.appointmentWindow.cutOffTimeMinutes,
        companyData.timezone
      );
      return appointmentHistoryData.appointmentWindow.cutOffTimeMinutes
        ? DateUtils.todayOrFormat(cutOffTime, {
            timezone: companyData.timezone,
            fullFormat: DateFormatEnum.DateTimeWeekday
          })
        : 'N/A';
    })()
  },
  { label: 'Window Quantity', textOrComponent: `${appointmentHistoryData.appointmentWindow.quantity}` },
  {
    label: 'Appointment Date',
    textOrComponent: (
      <IconTypography
        startIcon={<EventIcon color='primary' />}
        text={DateUtils.todayOrFormat(appointmentHistoryData.date, {
          timezone: companyData.timezone,
          fullFormat: DateFormatEnum.DateTimeWeekday
        })}
      />
    )
  },
  { label: 'Status', textOrComponent: `${AppointmentStatusEnum[appointmentHistoryData.status]}` },
  { label: 'Trade', textOrComponent: <TradesDisplay trades={[appointmentHistoryData.trade]} color='primary' /> },
  {
    label: 'Zone',
    textOrComponent: !appointmentHistoryData.companyZoneId
      ? 'N/A'
      : (companyData?.zones || []).find((z) => z.id === appointmentHistoryData.companyZoneId)?.title || 'Inactive Zone'
  }
];

const StyledTab = (props: TabProps) => {
  const { sx, ...otherProps } = props;
  return (
    <Tab
      sx={[
        (theme) => ({
          textTransform: 'none',
          minHeight: 0,
          borderRadius: '8px',
          paddingY: 0.75,
          color: theme.palette.primary.main,
          '&.MuiTab-root.Mui-selected, &:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1)
          }
        }),
        ...toSxArray(sx)
      ]}
      {...otherProps}
    />
  );
};

type PhoneCallDetailProps = {
  appDrawerState?: AppDrawerState;
  callCenterTimeZone: TimezoneAbbreviationEnum;
  id: string;
};

export const PhoneCallDetail = (props: PhoneCallDetailProps) => {
  const { appDrawerState, callCenterTimeZone, id } = props;

  const navigate = useNavigate();
  const location = useLocation();

  const api = useContext(ApiContext);

  const { authUserCacheResource } = useContext(AuthContext);
  const authUserQuery = authUserCacheResource.useCacheQuery();

  const singleColumn = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const gridItemSize = singleColumn ? 12 : 6;

  // https://stackoverflow.com/a/56743659
  const [scrollTarget, setScrollTarget] = useState<HTMLDivElement | undefined>(undefined);
  const scrollTrigger = useScrollTrigger({ target: scrollTarget, disableHysteresis: true, threshold: 0 });

  const generalCallScrollRef = useRef<HTMLDivElement>(null);
  const companyScrollRef = useRef<HTMLDivElement>(null);
  const customerScrollRef = useRef<HTMLDivElement>(null);
  const aiCallScrollRef = useRef<HTMLDivElement>(null);
  const jobScrollRef = useRef<HTMLDivElement>(null);
  const appointmentScrollRef = useRef<HTMLDivElement>(null);
  const previousAppointmentScrollRef = useRef<HTMLDivElement>(null);
  const callSegmentsScrollRef = useRef<HTMLDivElement>(null);

  const phoneCallDetailQuery = api.phoneCallsV1.useGetById({ id });
  const phoneCallDetailData = phoneCallDetailQuery.data;

  const companyQuery = api.companies.useGetById(
    phoneCallDetailData ? { id: phoneCallDetailData.companyId } : { skipQuery: true }
  );
  const companyData = companyQuery.data;

  const accountUserQuery = api.accountUsers.useGetById(
    phoneCallDetailData && phoneCallDetailData.agentType === AgentTypeEnum.Account
      ? { id: phoneCallDetailData.createdById, extraOptions: { disableSnackOnNotFoundError: true } }
      : { skipQuery: true }
  );
  const userQuery = api.users.useGetById(
    phoneCallDetailData && phoneCallDetailData.agentType !== AgentTypeEnum.Account
      ? { id: phoneCallDetailData.createdById, extraOptions: { disableSnackOnNotFoundError: true } }
      : { skipQuery: true }
  );
  const userData = userQuery.data || accountUserQuery.data;

  /** Fetch the employee from the legacy phoneCall.job, if applicable */
  const employeeQuery = api.employees.useGetById(
    phoneCallDetailData && phoneCallDetailData.job?.employeeId
      ? { id: phoneCallDetailData.job.employeeId }
      : { skipQuery: true }
  );

  /** For legacy no-provider/SWAPI calls */
  const pointmanOrSwapiCustomerQuery = api.pointmanCustomers.useGetById(
    phoneCallDetailData?.customerId && companyData
      ? { id: phoneCallDetailData.customerId, params: { companyId: companyData.id } }
      : { skipQuery: true }
  );

  const appointmentHistoryQuery = api.appointmentHistory.useGetById(
    phoneCallDetailData && phoneCallDetailData.appointmentHistoryId
      ? { id: phoneCallDetailData.appointmentHistoryId }
      : { skipQuery: true }
  );

  const previousAppointmentHistoryQuery = api.appointmentHistory.useGetById(
    phoneCallDetailData && phoneCallDetailData.previousAppointmentHistoryId
      ? { id: phoneCallDetailData.previousAppointmentHistoryId }
      : { skipQuery: true }
  );

  const callSegmentsQuery = api.callSegments.useUnpagedList(
    {
      params: {
        phoneCallId: phoneCallDetailData?.id || undefined,
        telephonyId: phoneCallDetailData?.telephonyId || undefined
      }
    },
    { enabled: !!phoneCallDetailData }
  );

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
      <BackdropSpinner
        open={
          phoneCallDetailQuery.isFetching ||
          accountUserQuery.isFetching ||
          userQuery.isFetching ||
          companyQuery.isFetching ||
          appointmentHistoryQuery.isFetching ||
          previousAppointmentHistoryQuery.isFetching ||
          employeeQuery.isFetching ||
          pointmanOrSwapiCustomerQuery.isFetching ||
          callSegmentsQuery.isFetching
        }
      />
      <SimpleAppBar
        title='Call Details'
        onBackClicked={() => {
          const locationState = location.state;
          if (hasGoBack(locationState)) {
            navigate(locationState.goBack.url);
          } else {
            navigate(getFullAppRoutePath(appRoutePaths.CallLog));
          }
        }}
        onAppDrawerToggleClicked={appDrawerState?.openSlidingDrawer}
      />
      <ActionBar
        actions={
          <Tabs
            centered={false}
            variant='scrollable'
            scrollButtons='auto'
            allowScrollButtonsMobile
            value={false}
            sx={{ alignItems: 'center', minHeight: 0 }}>
            <StyledTab
              icon={<PhoneIcon />}
              iconPosition='start'
              label='General Call Info'
              onClick={() => {
                generalCallScrollRef.current?.scrollIntoView();
              }}
            />
            <StyledTab
              icon={<BusinessIcon />}
              iconPosition='start'
              label='Company'
              onClick={() => {
                companyScrollRef.current?.scrollIntoView();
              }}
            />
            {phoneCallDetailData?.customerDisplayName && (
              <StyledTab
                icon={<PersonIcon />}
                iconPosition='start'
                label='Customer'
                onClick={() => {
                  customerScrollRef.current?.scrollIntoView();
                }}
              />
            )}
            {phoneCallDetailData?.aiCallId && (
              <StyledTab
                icon={<AiCallIcon />}
                iconPosition='start'
                label='CPI Call Info'
                onClick={() => {
                  aiCallScrollRef.current?.scrollIntoView();
                }}
              />
            )}
            {(phoneCallDetailData?.job ||
              (phoneCallDetailData?.providerData &&
                (isServiceTitanApiPhoneCall(phoneCallDetailData) ||
                  isServiceTitanPortalV2PhoneCall(phoneCallDetailData)) &&
                phoneCallDetailData.providerData.job)) && (
              <StyledTab
                icon={<WorkIcon />}
                iconPosition='start'
                label='Job'
                onClick={() => {
                  jobScrollRef.current?.scrollIntoView();
                }}
              />
            )}
            {appointmentHistoryQuery.data && (
              <StyledTab
                icon={<EventIcon />}
                iconPosition='start'
                label='Appointment'
                onClick={() => {
                  appointmentScrollRef.current?.scrollIntoView();
                }}
              />
            )}
            {previousAppointmentHistoryQuery.data && (
              <StyledTab
                icon={<EventRepeatIcon />}
                iconPosition='start'
                label='Previous Appointment'
                onClick={() => {
                  previousAppointmentScrollRef.current?.scrollIntoView();
                }}
              />
            )}
            {callSegmentsQuery.data && callSegmentsQuery.data.length > 0 && (
              <StyledTab
                icon={<SwapCallsIcon />}
                iconPosition='start'
                label='Call Segments'
                onClick={() => {
                  callSegmentsScrollRef.current?.scrollIntoView();
                }}
              />
            )}
          </Tabs>
        }
      />
      <Divider />
      {phoneCallDetailData && companyData && (
        <>
          <FlexBox sx={{ overflow: 'hidden', height: '100%', width: '100%' }}>
            <Paper
              variant='elevation'
              elevation={3}
              ref={(node) => {
                if (node) {
                  setScrollTarget(node);
                }
              }}
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.background.default,
                borderRadius: 0,
                padding: 3,
                width: singleColumn ? '100%' : '66%',
                height: '100%',
                overflow: 'auto'
              })}>
              <Stack useFlexGap spacing={2}>
                <Card ref={generalCallScrollRef}>
                  <PrimaryCardHeader
                    title={<IconTypography variant='h3' startIcon={<PhoneIcon />} text='General Call Info' />}
                  />
                  <KeyValueGrid
                    gridItemSize={gridItemSize}
                    padding={2}
                    keyValueArray={[
                      { label: 'Outcome', textOrComponent: phoneCallDetailData.outcome },
                      {
                        label: 'Caller ID',
                        textOrComponent: (
                          <IconTypography
                            startIcon={<PhoneCallbackIcon color='primary' />}
                            text={formatPhone(phoneCallDetailData.callerId)}
                          />
                        )
                      },
                      {
                        label: `Date \u0026 Time`,
                        textOrComponent: (
                          <IconTypography
                            startIcon={<EventIcon color='primary' />}
                            text={`${DateUtils.todayOrFormat(phoneCallDetailData.createdAt, {
                              timezone: callCenterTimeZone,
                              fullFormat: DateFormatEnum.DateTimeWeekdayShort
                            })}`}
                          />
                        )
                      },
                      ...(phoneCallDetailData.appointmentDate
                        ? [
                            {
                              label: 'Appointment Date',
                              textOrComponent: (
                                <IconTypography
                                  startIcon={<EventIcon color='primary' />}
                                  text={DateUtils.todayOrFormat(
                                    phoneCallDetailData.appointmentDate,

                                    {
                                      timezone: callCenterTimeZone,
                                      fullFormat: DateFormatEnum.DateTimeWeekdayShort
                                    }
                                  )}
                                />
                              )
                            }
                          ]
                        : []),
                      ...(phoneCallDetailData.previousAppointmentDateTime || phoneCallDetailData.previousAppointmentDate
                        ? [
                            {
                              label: 'Previous Appointment Date',
                              textOrComponent: (
                                <IconTypography
                                  startIcon={<EventRepeatIcon color='primary' />}
                                  text={
                                    phoneCallDetailData.previousAppointmentDateTime
                                      ? DateUtils.todayOrFormat(phoneCallDetailData.previousAppointmentDateTime, {
                                          timezone: callCenterTimeZone,
                                          fullFormat: DateFormatEnum.DateTimeWeekdayShort
                                        })
                                      : phoneCallDetailData.previousAppointmentDate
                                        ? DateUtils.format(
                                            phoneCallDetailData.previousAppointmentDate,
                                            DateFormatEnum.DateWeekday,
                                            {
                                              timezone: callCenterTimeZone
                                            }
                                          )
                                        : ''
                                  }
                                />
                              )
                            }
                          ]
                        : []),
                      {
                        label: 'Duration',
                        textOrComponent: (
                          <IconTypography
                            startIcon={<AvTimerIcon color='primary' />}
                            text={`${DateUtils.toPrettyDuration(phoneCallDetailData.duration)}`}
                          />
                        )
                      },
                      {
                        label: 'Manual Call',
                        textOrComponent: phoneCallDetailData.isManualCall ? 'Yes' : 'No'
                      },
                      {
                        label: 'Agent',
                        textOrComponent: (
                          <IconTypography
                            startIcon={<SupportAgentIcon color='primary' />}
                            text={getCallAgentDisplayName(userData)}
                          />
                        )
                      },
                      {
                        label: 'Agent Type',
                        textOrComponent: phoneCallDetailData.agentType
                      },
                      ...(phoneCallDetailData.trade
                        ? [
                            {
                              label: 'Trade',
                              textOrComponent: <TradesDisplay trades={[phoneCallDetailData.trade]} color='primary' />
                            }
                          ]
                        : []),
                      ...(phoneCallDetailData.serviceType
                        ? [
                            {
                              label: 'Service Type',
                              textOrComponent: ServiceTypeEnum[phoneCallDetailData.serviceType]
                            }
                          ]
                        : [])
                    ]}
                  />
                </Card>
                <Card ref={companyScrollRef}>
                  <PrimaryCardHeader
                    title={
                      <FlexBox sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <IconTypography variant='h3' startIcon={<BusinessIcon />} text='Company' />
                        {authUserQuery.data && ManageCompanies({ user: authUserQuery.data }) && (
                          <Tooltip title='View Company Details' placement='top'>
                            <IconButton
                              size='small'
                              onClick={() => {
                                window.open(
                                  `${getFullAppRoutePath(appRoutePaths.Company)}/${companyData.id}`,
                                  '_blank'
                                );
                              }}>
                              <OpenInNewIcon color='primaryInverted' />
                            </IconButton>
                          </Tooltip>
                        )}
                      </FlexBox>
                    }
                  />
                  <PhoneCallCompanyTabs
                    companyData={companyData}
                    phoneCallDetailData={phoneCallDetailData}
                    gridItemSize={gridItemSize}
                  />
                </Card>
                {(isServiceTitanApiPhoneCall(phoneCallDetailData) ||
                  isStCapacityPhoneCall(phoneCallDetailData) ||
                  isServiceTitanPortalV2PhoneCall(phoneCallDetailData)) &&
                  phoneCallDetailData.providerData &&
                  phoneCallDetailData.providerData.customer && (
                    <Card ref={customerScrollRef}>
                      <PrimaryCardHeader
                        title={<IconTypography variant='h3' startIcon={<PersonIcon />} text='Customer' />}
                      />
                      <Stack padding={2} useFlexGap spacing={2}>
                        {phoneCallDetailData.providerData.customer.isDNS === true && (
                          <Chip
                            label='DNS'
                            size='small'
                            color='error'
                            icon={<ReportIcon />}
                            sx={{ maxWidth: 'fit-content' }}
                          />
                        )}
                        <KeyValueGrid
                          gridItemSize={gridItemSize}
                          keyValueArray={[
                            {
                              label: phoneCallDetailData.providerData.customer.companyName
                                ? phoneCallDetailData.providerData.customer.companyName
                                : `${phoneCallDetailData.providerData.customer.firstName} ${phoneCallDetailData.providerData.customer.lastName}`.trim(),
                              textOrComponent: (
                                <IconTypography
                                  startIcon={<PersonIcon color='primary' />}
                                  text={formatAddressTwoLine(phoneCallDetailData.providerData.customer)}
                                />
                              )
                            },
                            ...(phoneCallDetailData.providerData.location
                              ? [
                                  {
                                    label: 'Location',
                                    textOrComponent: (
                                      <IconTypography
                                        startIcon={<LocationPinIcon color='primary' />}
                                        text={`${phoneCallDetailData.providerData.location.name}
                                               ${formatAddressOneLine(phoneCallDetailData.providerData.location)}`}
                                        sx={{ whiteSpace: 'pre-line' }}
                                      />
                                    )
                                  }
                                ]
                              : [])
                          ]}
                        />
                        <PhoneCallStCustomerDetail
                          providerCustomerId={phoneCallDetailData.providerData.customer.providerId}
                          companyData={companyData}
                          membershipDisplay={
                            <>
                              {(() => {
                                const matchedLocationSingleMemberships: ServiceTitanPhoneCallCustomerMembership[] = [];
                                const matchedLocationAllMemberships: ServiceTitanPhoneCallCustomerMembership[] = [];
                                const unmatchedLocationAllMemberships: ServiceTitanPhoneCallCustomerMembership[] = [];
                                const unmatchedLocationSingleMemberships: ServiceTitanPhoneCallCustomerMembership[] =
                                  [];

                                const providerLocationId =
                                  phoneCallDetailData.providerData.location &&
                                  phoneCallDetailData.providerData.location.providerId;
                                phoneCallDetailData.providerData.customerMemberships?.forEach((m) => {
                                  const isMatchedLocation = !!(
                                    providerLocationId && providerLocationId === m.providerLocationId
                                  );
                                  const isSingle =
                                    m.membershipType && m.membershipType.locationTarget === 'SingleLocation';

                                  if (isMatchedLocation && isSingle) {
                                    matchedLocationSingleMemberships.push(m);
                                  } else if (isMatchedLocation) {
                                    matchedLocationAllMemberships.push(m);
                                  } else if (!isSingle) {
                                    unmatchedLocationAllMemberships.push(m);
                                  } else {
                                    unmatchedLocationSingleMemberships.push(m);
                                  }
                                });

                                const sortedServiceTitanMemberships = matchedLocationSingleMemberships
                                  .concat(matchedLocationAllMemberships)
                                  .concat(unmatchedLocationAllMemberships)
                                  .concat(unmatchedLocationSingleMemberships);

                                return !sortedServiceTitanMemberships.length ? (
                                  <Typography text='No active memberships recorded at the time of the call' />
                                ) : (
                                  <List
                                    disablePadding
                                    sx={(theme) => ({
                                      marginTop: 1,
                                      border: 1,
                                      borderRadius: 2,
                                      borderColor: theme.palette.divider
                                    })}>
                                    {sortedServiceTitanMemberships.map((membership, index, array) => (
                                      <ListItem key={index} divider={index !== array.length - 1}>
                                        <Stack spacing={0}>
                                          <Typography>
                                            {`${membership.membershipType ? `${membership.membershipType.name} (${membership.membershipType.locationTarget})` : 'Unknown Membership Type'} - `}
                                            <Typography
                                              component='span'
                                              color={
                                                membership.status === ST.CustomerMembershipStatusEnum.Active
                                                  ? 'success'
                                                  : 'error'
                                              }
                                              text={membership.status}
                                            />
                                          </Typography>
                                          {membership.from && (
                                            <Typography>
                                              <b>Start: </b>
                                              {DateUtils.format(membership.from, DateFormatEnum.DateTimeWeekdayShort, {
                                                timezone: companyData.timezone,
                                                showTimezone: true
                                              })}
                                            </Typography>
                                          )}
                                          {membership.to && (
                                            <Typography>
                                              <b>End: </b>
                                              {DateUtils.format(membership.to, DateFormatEnum.DateTimeWeekdayShort, {
                                                timezone: companyData.timezone,
                                                showTimezone: true
                                              })}
                                            </Typography>
                                          )}
                                          {membership.duration && (
                                            <Typography>
                                              <b>Duration: </b>
                                              {membership.duration} months
                                            </Typography>
                                          )}
                                          {membership.renewalDuration && (
                                            <Typography>
                                              <b>Renewal Duration: </b>
                                              {membership.renewalDuration} months
                                            </Typography>
                                          )}
                                          {membership.cancellationDate && (
                                            <Typography>
                                              <b>Cancel Date: </b>
                                              {DateUtils.todayOrFormat(membership.cancellationDate, {
                                                timezone: companyData.timezone,
                                                fullFormat: DateFormatEnum.DateTimeWeekdayShort
                                              })}
                                            </Typography>
                                          )}
                                          {membership.note && (
                                            <Typography component='span' display='flex' flexWrap='wrap'>
                                              <b>Notes:</b>&nbsp;
                                              <ShowMoreText text={membership.note} isRichText={true} />
                                            </Typography>
                                          )}
                                        </Stack>
                                      </ListItem>
                                    ))}
                                  </List>
                                );
                              })()}
                            </>
                          }
                        />
                      </Stack>
                    </Card>
                  )}
                {isPoolBrainPhoneCall(phoneCallDetailData) &&
                  phoneCallDetailData.providerData &&
                  (phoneCallDetailData.providerData.customer || phoneCallDetailData.providerData.location) && (
                    <Card ref={customerScrollRef}>
                      <PrimaryCardHeader
                        title={<IconTypography variant='h3' startIcon={<PersonIcon />} text='Customer' />}
                      />
                      <KeyValueGrid
                        padding={2}
                        gridItemSize={gridItemSize}
                        keyValueArray={[
                          ...(phoneCallDetailData.providerData.customer
                            ? [
                                {
                                  label:
                                    phoneCallDetailData.providerData.customer.name ||
                                    phoneCallDetailData.customerDisplayName ||
                                    'Unknown Customer',
                                  textOrComponent: (
                                    <Stack spacing={0}>
                                      {!!phoneCallDetailData.providerData.customer.phoneNumber && (
                                        <IconTypography
                                          startIcon={<PhoneIcon color='primary' />}
                                          text={phoneCallDetailData.providerData.customer.phoneNumber}
                                        />
                                      )}
                                      {!!phoneCallDetailData.providerData.customer.email && (
                                        <IconTypography
                                          startIcon={<EmailIcon color='primary' />}
                                          text={phoneCallDetailData.providerData.customer.email}
                                        />
                                      )}
                                    </Stack>
                                  )
                                }
                              ]
                            : []),
                          ...(phoneCallDetailData.providerData.location
                            ? [
                                {
                                  label: 'Customer Location',
                                  textOrComponent: (
                                    <Stack>
                                      <IconTypography
                                        startIcon={<LocationPinIcon color='primary' />}
                                        text={formatAddressTwoLine(phoneCallDetailData.providerData.location)}
                                        sx={{ whiteSpace: 'pre-line' }}
                                      />
                                      {(phoneCallDetailData.providerData.location.isBilling ||
                                        phoneCallDetailData.providerData.location.isPrimary) && (
                                        <Stack direction='row'>
                                          {phoneCallDetailData.providerData.location.isBilling && (
                                            <Chip label='Billing' size='medium' color='primary' />
                                          )}
                                          {phoneCallDetailData.providerData.location.isPrimary && (
                                            <Chip label='Primary' size='medium' color='primary' />
                                          )}
                                        </Stack>
                                      )}
                                    </Stack>
                                  )
                                }
                              ]
                            : [])
                        ]}
                      />
                    </Card>
                  )}
                {!isServiceTitanApiPhoneCall(phoneCallDetailData) &&
                  !isServiceTitanPortalV2PhoneCall(phoneCallDetailData) &&
                  !isPoolBrainPhoneCall(phoneCallDetailData) &&
                  !!pointmanOrSwapiCustomerQuery.data && (
                    <Card ref={customerScrollRef}>
                      <PrimaryCardHeader
                        title={<IconTypography variant='h3' startIcon={<PersonIcon />} text='Customer' />}
                      />
                      <Stack padding={2} useFlexGap spacing={2}>
                        {(isSwapiV1PhoneCall(phoneCallDetailData) || isSwapiV2PhoneCall(phoneCallDetailData)) && (
                          <InfoAlert
                            sx={{ justifyContent: 'center', alignItems: 'center', padding: 0 }}
                            icon={<BoltIcon />}>
                            <Typography text={'All customer info is pulled live from SWAPI'} />
                          </InfoAlert>
                        )}
                        {pointmanOrSwapiCustomerQuery.data.isDNS === true && (
                          <Chip
                            label='DNS'
                            size='small'
                            color='error'
                            icon={<ReportIcon />}
                            sx={{ maxWidth: 'fit-content' }}
                          />
                        )}
                        <HeaderBox
                          headerText={
                            pointmanOrSwapiCustomerQuery.data.isCommercial
                              ? pointmanOrSwapiCustomerQuery.data.companyName || 'Unknown Company'
                              : pointmanOrSwapiCustomerQuery.data.fullName || 'Unknown Customer'
                          }>
                          {pointmanOrSwapiCustomerQuery.data.isCommercial &&
                            pointmanOrSwapiCustomerQuery.data.fullName && (
                              <Typography text={pointmanOrSwapiCustomerQuery.data.fullName} />
                            )}
                          {!pointmanOrSwapiCustomerQuery.data.isCommercial &&
                            pointmanOrSwapiCustomerQuery.data.companyName && (
                              <Typography text={pointmanOrSwapiCustomerQuery.data.companyName} />
                            )}
                          <IconTypography
                            startIcon={<LocationPinIcon color='primary' />}
                            sx={{ whiteSpace: 'pre-line' }}
                            text={formatAddressTwoLine(pointmanOrSwapiCustomerQuery.data)}
                          />
                        </HeaderBox>
                        <KeyValueGrid
                          gridItemSize={gridItemSize}
                          keyValueArray={[
                            {
                              label: 'Phone Number',
                              textOrComponent: (
                                <IconTypography
                                  startIcon={<PhoneIcon color='primary' />}
                                  text={formatPhone(pointmanOrSwapiCustomerQuery.data.phoneNumber) || 'N/A'}
                                />
                              )
                            },
                            {
                              label: 'Email',
                              textOrComponent: (
                                <IconTypography
                                  startIcon={<EmailIcon color='primary' />}
                                  text={pointmanOrSwapiCustomerQuery.data.email || 'N/A'}
                                />
                              )
                            },
                            {
                              label: 'Secondary Contact',
                              textOrComponent: (
                                <Stack useFlexGap spacing={0}>
                                  {(() => {
                                    const returnValues = [];
                                    if (pointmanOrSwapiCustomerQuery.data.secondaryContact?.name) {
                                      returnValues.push(
                                        <Typography text={pointmanOrSwapiCustomerQuery.data.secondaryContact.name} />
                                      );
                                    }
                                    if (pointmanOrSwapiCustomerQuery.data.secondaryContact?.phoneNumber) {
                                      returnValues.push(
                                        <IconTypography
                                          startIcon={<PhoneIcon color='primary' />}
                                          text={formatPhone(
                                            pointmanOrSwapiCustomerQuery.data.secondaryContact.phoneNumber
                                          )}
                                        />
                                      );
                                    }
                                    if (!returnValues.length) {
                                      return <Typography text='N/A' />;
                                    }
                                    return returnValues;
                                  })()}
                                </Stack>
                              )
                            },
                            {
                              label: 'Ownership',
                              textOrComponent: pointmanOrSwapiCustomerQuery.data.isOwner
                                ? 'Property Owner'
                                : 'Not Property Owner'
                            },
                            {
                              label: 'Lead Source Indicator',
                              textOrComponent: pointmanOrSwapiCustomerQuery.data.leadSourceIndicator || 'N/A'
                            },
                            ...(!isSwapiV1PhoneCall(phoneCallDetailData) &&
                            !isSwapiV2PhoneCall(phoneCallDetailData) &&
                            pointmanOrSwapiCustomerQuery.data.billingContact?.fullName
                              ? [
                                  {
                                    label: 'Billing Contact',
                                    textOrComponent: (
                                      <Stack useFlexGap spacing={0}>
                                        <Typography text={pointmanOrSwapiCustomerQuery.data.billingContact.fullName} />
                                        <TextBlockTypography
                                          text={formatAddressTwoLine(pointmanOrSwapiCustomerQuery.data.billingContact)}
                                        />
                                        {pointmanOrSwapiCustomerQuery.data.billingContact.phoneNumber && (
                                          <IconTypography
                                            startIcon={<PhoneIcon color='primary' />}
                                            text={formatPhone(
                                              pointmanOrSwapiCustomerQuery.data.billingContact.phoneNumber
                                            )}
                                          />
                                        )}
                                        {pointmanOrSwapiCustomerQuery.data.billingContact.email && (
                                          <IconTypography
                                            startIcon={<EmailIcon color='primary' />}
                                            text={pointmanOrSwapiCustomerQuery.data.billingContact.email}
                                          />
                                        )}
                                      </Stack>
                                    )
                                  }
                                ]
                              : [])
                          ]}
                        />
                        <HeaderBox headerText='Notes'>
                          <Stack direction='row'>
                            <SpeakerNotes />
                            <ShowMoreText text={pointmanOrSwapiCustomerQuery.data.notes || 'N/A'} />
                          </Stack>
                        </HeaderBox>
                      </Stack>
                    </Card>
                  )}
                {phoneCallDetailData.aiCallId && (
                  // TODO: update Card component to use flex instead of block
                  <Card ref={aiCallScrollRef} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <PrimaryCardHeader
                      title={<IconTypography variant='h3' startIcon={<AutoAwesomeIcon />} text='CPI Call Info' />}
                    />
                    <PhoneCallAiCall
                      aiCallId={phoneCallDetailData.aiCallId}
                      gridItemSize={gridItemSize}
                      timezone={companyData.timezone}
                    />
                  </Card>
                )}
                {/* Job & assigned Tech Information before we implemented phoneCall.providerData.job (SWAPI) */}
                {(isSwapiV1PhoneCall(phoneCallDetailData) || isSwapiV2PhoneCall(phoneCallDetailData)) &&
                  phoneCallDetailData.job?.jobNo && (
                    <Card ref={jobScrollRef}>
                      <PrimaryCardHeader
                        title={<IconTypography variant='h3' startIcon={<WorkIcon />} text='SWAPI Job' />}
                      />
                      <Stack padding={2} spacing={2}>
                        <KeyValueGrid
                          gridItemSize={gridItemSize}
                          keyValueArray={[
                            {
                              label: 'Job Number',
                              textOrComponent: phoneCallDetailData.job.jobNo
                            },
                            ...(employeeQuery.data
                              ? [
                                  {
                                    label: 'Tech Name',
                                    textOrComponent: `${employeeQuery.data.firstName} ${employeeQuery.data.lastName}`
                                  },
                                  {
                                    label: 'Tech Provider Identifier',
                                    textOrComponent: employeeQuery.data.providerId
                                  }
                                ]
                              : [])
                          ]}
                        />
                        {phoneCallDetailData.billingAccount && (
                          <>
                            <HeaderBox headerText='Billing Account' sx={{ flexDirection: 'column' }}>
                              <IconTypography
                                startIcon={<PersonIcon color='primary' />}
                                text={`${phoneCallDetailData.billingAccount.firstName} ${phoneCallDetailData.billingAccount.lastName}`}
                              />
                              <IconTypography
                                startIcon={<PhoneIcon color='primary' />}
                                text={formatPhone(phoneCallDetailData.callerId)}
                              />
                              <IconTypography
                                startIcon={<LocationPinIcon color='primary' />}
                                text={formatAddressOneLine(phoneCallDetailData.billingAccount)}
                              />
                            </HeaderBox>
                            <KeyValueGrid
                              gridItemSize={gridItemSize}
                              keyValueArray={[
                                ...(() => {
                                  // TODO: move this somewhere else if we need it
                                  const currencyFormatter = Intl.NumberFormat('en-ES', {
                                    currency: 'USD',
                                    style: 'currency',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4,
                                    minimumIntegerDigits: 1
                                  });

                                  const depositOnAccount = phoneCallDetailData.billingAccount.depositOnAccount || '0';
                                  const depositOnAccountFloat = parseFloat(depositOnAccount);
                                  const formattedDeposit = currencyFormatter.format(depositOnAccountFloat);

                                  const currentBalance = phoneCallDetailData.billingAccount.currentBalance || '0';
                                  const currentBalanceFloat = parseFloat(currentBalance);
                                  const formattedCurrentBalance = currencyFormatter.format(currentBalanceFloat);

                                  return [
                                    { label: 'Deposit on Account', textOrComponent: formattedDeposit },
                                    { label: 'Account Balance', textOrComponent: formattedCurrentBalance }
                                  ];
                                })()
                              ]}
                            />
                            {phoneCallDetailData.billingAccount.customerRating === 'BADR' && (
                              <Typography text='Bad Risk Account' color='error' />
                            )}
                            <HeaderBox headerText='Billing Account Notes'>
                              <Stack direction='row'>
                                <SpeakerNotes />
                                <ShowMoreText text={phoneCallDetailData.billingAccount.notes || 'N/A'} />
                              </Stack>
                            </HeaderBox>
                          </>
                        )}
                      </Stack>
                    </Card>
                  )}
                {phoneCallDetailData.providerData &&
                  (isServiceTitanApiPhoneCall(phoneCallDetailData) ||
                    isServiceTitanPortalV2PhoneCall(phoneCallDetailData)) &&
                  phoneCallDetailData.providerData.job && (
                    <Card ref={jobScrollRef}>
                      <PrimaryCardHeader
                        title={<IconTypography variant='h3' startIcon={<WorkIcon />} text='Service Titan Job' />}
                      />
                      <Stack useFlexGap padding={2} spacing={2}>
                        <KeyValueGrid
                          gridItemSize={gridItemSize}
                          keyValueArray={[
                            { label: 'Job Number', textOrComponent: phoneCallDetailData.providerData.job.jobNo },
                            {
                              label: 'Job Type',
                              textOrComponent: phoneCallDetailData.providerData.job.jobTypeName || 'N/A'
                            },
                            {
                              label: 'Business Unit',
                              textOrComponent: phoneCallDetailData.providerData.job.businessUnitName || 'N/A'
                            },
                            {
                              label: 'Campaign',
                              textOrComponent: phoneCallDetailData.providerData.job.campaignName || 'N/A'
                            },
                            { label: 'Status', textOrComponent: phoneCallDetailData.providerData.job.status },
                            { label: 'Priority', textOrComponent: phoneCallDetailData.providerData.job.priority },
                            ...(phoneCallDetailData.providerData.assignedEmployee
                              ? [
                                  {
                                    label: 'Tech Name',
                                    textOrComponent: `${phoneCallDetailData.providerData.assignedEmployee.firstName} ${phoneCallDetailData.providerData.assignedEmployee.lastName}`
                                  }
                                ]
                              : []),
                            ...(isServiceTitanApiPhoneCall(phoneCallDetailData) &&
                            phoneCallDetailData.providerData.jobCancelReason
                              ? [
                                  {
                                    label: 'Cancel Reason',
                                    textOrComponent: phoneCallDetailData.providerData.jobCancelReason.name
                                  }
                                ]
                              : [])
                          ]}
                        />
                        {isServiceTitanPortalV2PhoneCall(phoneCallDetailData) &&
                          phoneCallDetailData.providerData.jobCancelEvent && (
                            <>
                              <KeyValueGrid
                                gridItemSize={gridItemSize}
                                keyValueArray={[
                                  {
                                    label: 'Cancel Reason',
                                    textOrComponent: phoneCallDetailData.providerData.jobCancelEvent.name
                                  },
                                  {
                                    label: 'Cancel Date',
                                    textOrComponent: DateUtils.todayOrFormat(
                                      phoneCallDetailData.providerData.jobCancelEvent.date,
                                      {
                                        timezone: companyData.timezone,
                                        fullFormat: DateFormatEnum.DateTimeWeekdayShort
                                      }
                                    )
                                  }
                                ]}
                              />
                              <HeaderBox headerText='Cancel Notes'>
                                <Stack direction='row'>
                                  <SpeakerNotes />
                                  <ShowMoreText
                                    text={phoneCallDetailData.providerData.jobCancelEvent.notes}
                                    isRichText={true}
                                  />
                                </Stack>
                              </HeaderBox>
                            </>
                          )}
                        {isServiceTitanPortalV2PhoneCall(phoneCallDetailData) &&
                          phoneCallDetailData.providerData.job.notes && (
                            <HeaderBox headerText='Summary' sx={{ flexDirection: 'row' }}>
                              <Stack direction='row'>
                                <SpeakerNotes />
                                <ShowMoreText text={phoneCallDetailData.providerData.job.notes} isRichText={true} />
                              </Stack>
                            </HeaderBox>
                          )}
                        {isServiceTitanPortalV2PhoneCall(phoneCallDetailData) &&
                          phoneCallDetailData.providerData.jobHistoryNotes?.length && (
                            <HeaderBox
                              headerText='Job History Notes'
                              syncIconText='Job history notes added in Service Titan during the call'>
                              <NoteList
                                notes={sortedNotes(phoneCallDetailData.providerData.jobHistoryNotes)}
                                timezone={companyData.timezone}
                                displayShowMore
                                displayAsToday
                                divider
                                disablePadding
                                sx={(theme) => ({
                                  marginTop: 1,
                                  border: 1,
                                  borderRadius: 2,
                                  borderColor: theme.palette.divider
                                })}
                              />
                            </HeaderBox>
                          )}
                        {phoneCallDetailData.providerData.appointment && (
                          <HeaderBox
                            headerText={`Service Titan Appointment (Selected for ${phoneCallDetailData.outcome})`}>
                            <Stack useFlexGap spacing={0}>
                              {PhoneCallStAppointment(
                                phoneCallDetailData.providerData.appointment,
                                phoneCallDetailData.providerData.job?.jobNo || '',
                                companyData.timezone
                              )}
                              {phoneCallDetailData.providerData &&
                                isServiceTitanPortalV2PhoneCall(phoneCallDetailData) &&
                                phoneCallDetailData.providerData.appointment.specialInstructions && (
                                  <Typography component='span' display='flex' flexWrap='wrap'>
                                    <b>Special Instructions:</b>&nbsp;
                                    <ShowMoreText
                                      text={phoneCallDetailData.providerData.appointment.specialInstructions}
                                      isRichText={true}
                                    />
                                  </Typography>
                                )}
                              {/**
                               * NOTE - 'otherAppointments' can only exist if we have a Job,
                               * AND more importantly there are 'other' appointments for the job!
                               */}
                              {phoneCallDetailData.providerData &&
                                isServiceTitanPortalV2PhoneCall(phoneCallDetailData) &&
                                !!phoneCallDetailData.providerData.otherAppointments?.length && (
                                  <HeaderBox
                                    headerText='Other Appointments'
                                    syncIconText='Non-selected appointments modified in Service Titan during the call'
                                    sx={{ paddingTop: 1 }}>
                                    <List
                                      disablePadding
                                      sx={(theme) => ({
                                        marginTop: 1,
                                        border: 1,
                                        borderRadius: 2,
                                        borderColor: theme.palette.divider
                                      })}>
                                      {phoneCallDetailData.providerData.otherAppointments
                                        .sort((a, b) =>
                                          typeof b.providerModifiedAt === 'string' &&
                                          typeof a.providerModifiedAt === 'string'
                                            ? Date.parse(b.providerModifiedAt) - Date.parse(a.providerModifiedAt)
                                            : b.providerModifiedAt.valueOf() - a.providerModifiedAt.valueOf()
                                        )
                                        .map((otherAppointment, index, array) => (
                                          <ListItem
                                            key={otherAppointment.providerId}
                                            divider={index !== array.length - 1}
                                            sx={{ display: 'flex', flexDirection: 'row' }}>
                                            <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center' }}>
                                              <FlexBox>
                                                <PendingActionsIcon />
                                              </FlexBox>
                                              <Stack spacing={0}>
                                                {PhoneCallStAppointment(
                                                  otherAppointment,
                                                  phoneCallDetailData.providerData?.job?.jobNo || '',
                                                  companyData.timezone
                                                )}
                                                {otherAppointment.specialInstructions && (
                                                  <Typography component='span' display='flex' flexWrap='wrap'>
                                                    <b>Special Instructions:</b>&nbsp;
                                                    <ShowMoreText
                                                      text={otherAppointment.specialInstructions}
                                                      isRichText={true}
                                                    />
                                                  </Typography>
                                                )}
                                              </Stack>
                                            </Stack>
                                          </ListItem>
                                        ))}
                                    </List>
                                  </HeaderBox>
                                )}
                            </Stack>
                          </HeaderBox>
                        )}
                      </Stack>
                    </Card>
                  )}
                {appointmentHistoryQuery.data && (
                  <Card ref={appointmentScrollRef}>
                    <PrimaryCardHeader
                      title={<IconTypography variant='h3' startIcon={<EventIcon />} text='RelateCX Appointment' />}
                    />
                    <KeyValueGrid
                      gridItemSize={gridItemSize}
                      padding={2}
                      keyValueArray={getCallLogAppointmentEntries(appointmentHistoryQuery.data, companyData)}
                    />
                  </Card>
                )}
                {previousAppointmentHistoryQuery.data && (
                  <Card ref={previousAppointmentScrollRef}>
                    <PrimaryCardHeader
                      title={
                        <IconTypography
                          variant='h3'
                          startIcon={<EventRepeatIcon />}
                          text='RelateCX Previous Appointment'
                        />
                      }
                    />
                    <KeyValueGrid
                      gridItemSize={gridItemSize}
                      padding={2}
                      keyValueArray={getCallLogAppointmentEntries(previousAppointmentHistoryQuery.data, companyData)}
                    />
                  </Card>
                )}
                {callSegmentsQuery.data && callSegmentsQuery.data.length > 0 && (
                  <CallSegmentListDisplay
                    callSegments={callSegmentsQuery.data}
                    currentCallSegmentId={phoneCallDetailData.id}
                    companyTimezone={companyData.timezone}
                    ref={callSegmentsScrollRef}
                  />
                )}
              </Stack>
              <Fade in={scrollTrigger}>
                <Tooltip title='Scroll to top'>
                  <Fab
                    size='small'
                    sx={{ position: 'sticky', bottom: '8px', alignSelf: 'flex-end' }}
                    onClick={() => {
                      scrollTarget?.scrollTo(0, 0);
                    }}>
                    <KeyboardArrowUpIcon />
                  </Fab>
                </Tooltip>
              </Fade>
            </Paper>
            {!singleColumn && (
              <FlexBox
                sx={{
                  width: '34%',
                  height: '100%',
                  flexDirection: 'column'
                }}>
                <PhoneCallTabs
                  phoneCallDetailData={phoneCallDetailData}
                  companyData={companyData}
                  pointmanOrSwapiCustomer={pointmanOrSwapiCustomerQuery.data}
                />
              </FlexBox>
            )}
          </FlexBox>
          {singleColumn && (
            <>
              <Accordion disableGutters>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color='primaryInverted' />}
                  sx={(theme) => ({
                    paddingY: 0,
                    color: theme.palette.primary.contrastText,
                    backgroundColor: theme.palette.primary.main
                  })}>
                  <Typography variant='h3' text='Call Notes and Caller History' />
                </AccordionSummary>
                <Divider />
                <AccordionDetails sx={{ padding: 0 }}>
                  <FlexBox
                    sx={{
                      width: '100%',
                      height: '35vh',
                      flexDirection: 'column'
                    }}>
                    <PhoneCallTabs
                      phoneCallDetailData={phoneCallDetailData}
                      companyData={companyData}
                      pointmanOrSwapiCustomer={pointmanOrSwapiCustomerQuery.data}
                    />
                  </FlexBox>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </>
      )}
    </FlexBox>
  );
};

export const PhoneCallDetailRoute = () => {
  const { appDrawerState } = useOutletContext<AppRouteContext>();
  const { id } = useParams() as { id: string };

  const { callCenterCacheResource } = useContext(AuthContext);
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  return (
    <>
      {cachedCallCenterQuery.data?.timezone && (
        <PhoneCallDetail
          appDrawerState={appDrawerState}
          callCenterTimeZone={cachedCallCenterQuery.data.timezone}
          id={id}
        />
      )}
    </>
  );
};
